import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Kanban, Plus, Shield } from 'lucide-react';
import { cn } from '@/app/components/ui/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { usePlatformModules } from '@/hooks/use-modules';
import { PURCHASING_MODULE_KEY } from '@/lib/purchasing';
import { MODULE_NAVIGATION_ITEMS } from './primary-navigation';

interface ModuleTab {
  key: string;
  moduleKey?: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  matchPaths: string[];
  requiresModule?: boolean;
}

const MODULE_TABS: ModuleTab[] = [
  {
    key: 'h3ightboard',
    label: 'H3ightboard',
    href: '/h3ightboard',
    icon: Kanban,
    matchPaths: ['/h3ightboard'],
  },
  {
    key: 'my-dashboard',
    label: 'My Dashboard',
    href: '/my-dashboard',
    icon: Home,
    matchPaths: ['/my-dashboard'],
  },
  ...MODULE_NAVIGATION_ITEMS.map((item) => ({
    ...item,
    requiresModule: true,
  })),
  {
    key: 'admin',
    label: 'Admin',
    href: '/dashboard',
    icon: Shield,
    matchPaths: [
      '/dashboard',
      '/admin',
      '/settings',
      '/profile',
      '/people',
      '/locations',
      '/documents',
      '/reports',
      '/inspections',
      '/alerts',
      '/platform/gear-models',
    ],
  },
];

export function ModuleTabs() {
  const location = useLocation();
  const permissions = usePermissions();
  const { isModuleEnabled } = usePlatformModules();
  const activeTabRef = useRef<HTMLAnchorElement>(null);

  const pathIn = (...prefixes: string[]) =>
    prefixes.some((prefix) =>
      location.pathname === prefix || location.pathname.startsWith(prefix + '/'),
    );

  const isTabActive = (tab: ModuleTab) => {
    if (pathIn('/requisitions')) {
      return isModuleEnabled('inventory')
        ? tab.moduleKey === 'inventory'
        : tab.moduleKey === PURCHASING_MODULE_KEY;
    }
    if (pathIn('/purchase-orders', '/vendors')) {
      return tab.moduleKey === PURCHASING_MODULE_KEY;
    }
    return tab.matchPaths.some(
      (path) =>
        path === location.pathname || location.pathname.startsWith(path + '/'),
    );
  };

  // A module tab needs the org to have the module on AND the user to hold an
  // effective role other than no_access for it (org Admins resolve to admin).
  const visibleTabs = MODULE_TABS.filter(
    (tab) =>
      !tab.requiresModule ||
      !tab.moduleKey ||
      (isModuleEnabled(tab.moduleKey) && permissions.hasModuleAccess(tab.moduleKey)),
  );
  // The Admin group (People, Locations, Documents, Alerts, Reports, Profile)
  // is for every role; only org Admins enter it at the org-wide Admin
  // Dashboard, everyone else enters it at People.
  const tabHref = (tab: ModuleTab) =>
    tab.key === 'admin' && !permissions.isAdmin ? '/people' : tab.href;
  const hasDisabledModules = MODULE_TABS.some(
    (tab) =>
      tab.requiresModule &&
      tab.moduleKey &&
      !isModuleEnabled(tab.moduleKey),
  );

  useEffect(() => {
    activeTabRef.current?.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: 'instant',
    });
  }, [location.pathname]);

  return (
    <nav
      aria-label="Module navigation"
      className="hidden lg:flex items-center gap-0 px-6 overflow-x-auto"
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {visibleTabs.map((tab) => {
        const active = isTabActive(tab);
        const Icon = tab.icon;
        return (
          <Link
            key={tab.key}
            to={tabHref(tab)}
            ref={active ? activeTabRef : undefined}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              active
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground hover:border-gray-300 dark:hover:border-muted-foreground',
            )}
          >
            <Icon className="h-4 w-4" />
            <span className="font-bold">{tab.label}</span>
          </Link>
        );
      })}

      {hasDisabledModules && (permissions.isAdmin || permissions.isManager) && (
        <Link
          to="/settings/upgrade"
          className="flex items-center gap-1 px-3 py-2 text-sm font-medium border-b-2 border-transparent text-gray-400 dark:text-muted-foreground hover:text-gray-600 dark:hover:text-foreground hover:border-gray-300 dark:hover:border-muted-foreground transition-colors whitespace-nowrap"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Module
        </Link>
      )}
    </nav>
  );
}
